// 고장 조치 이력 — 필드팀이 현장에서 손본 기록.
//
// 고객사 리포트(CustomerSheetReportsPage)와 **같은 문법**으로 읽히게 만든다:
// 상단 집계 카드 → 필터 줄 → 표(왼쪽 색 띠 + 배지) → 편집 창(삭제 2단계).
// 두 화면을 오가는 사람이 규칙을 두 번 배우지 않도록.
//
// 다른 점은 담는 것뿐이다 — 저쪽은 **매장이 올린 신고**, 이쪽은 **필드팀의 조치**.
// 그래서 카드가 등급(P1/P2/P3)이 아니라 상태(해결 완료·모니터링·…)다.
//
// 사이드바 항목이 아니라 **고객사 리포트 화면의 탭**이다(오너 결정 2026-09-14).
// 라우트는 그대로 둘이라 링크 공유·뒤로가기가 산다 — FieldReportTabs 참고.
//
// 시트의 `처리 결과` 열은 **화면에 없다**(오너 결정 2026-09-14). 값이 `상태` 와
// 겹쳐 두 가지 상태를 말하게 되고, 배지·필터·카드가 이미 `상태` 를 쓴다.
// 수집은 계속 받아 `outcome`·`rawJson` 에 남으므로 필요해지면 되살릴 수 있다.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  classifyIssueCategory,
  IssueCategory,
  kstDateTime,
  kstTodayYmd,
  REMEDIATION_MATCH_SOURCE_LABELS,
  REMEDIATION_STATUS_LABELS,
  RemediationStatus,
  type CreateFaultRemediationDto,
  type FaultRemediationDto,
  type FaultRemediationSortColumn,
  type ListFaultRemediationsQuery,
} from '@bhops/shared';
import { faultRemediationsApi } from '@/api/faultRemediations';
import { extractApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { Pagination } from '@/components/Pagination';
import { SortableTh, columnSortDir } from '@/components/SortableTh';
import { nextColumnSort, type ColumnSortKind, type ColumnSortState } from '@/lib/column-sort';
import { useToast } from '@/components/Toast';
import { useBackdropDismiss } from '@/components/use-backdrop-dismiss';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import { FieldReportTabs, fieldReportPage } from './FieldReportTabs';
import { StoreRobotChecklist, StoreSuggestInput, type PickedStore } from './StoreRobotFields';
import { CategoryCell, CategorySelect } from './IssueCategoryFields';
import { pill, REMEDIATION_ICON, REMEDIATION_ORDER, REMEDIATION_TONE } from './field-tones';
import { StatFilterCard } from './StatFilterCard';

// 2026-10-28 오너 "30건 단위로 페이지 나눠줘".
const PAGE_SIZE = 30;

/**
 * 머리를 눌러 정렬(2026-10-28 오너 — 로봇 목록 머리와 같은 방식). 정렬은 **서버**가 한다(쪽으로 나눠 받으므로
 * 한 쪽만 줄 세우면 틀린다). 글자 = 가나다순 → 역순 → 해제, 발생 일시 = 최신 → 과거 → 해제, 상태 · 구분 = 분류별 → 해제.
 * 증상 · 조치 내용 · 사진은 정렬하지 않는다. 아무것도 안 고르면 발생 일시 최신순.
 */
const SORT_KIND: Record<FaultRemediationSortColumn, ColumnSortKind> = {
  status: 'category',
  issueCategory: 'category',
  reportNo: 'text',
  store: 'text',
  robot: 'text',
  errorCode: 'text',
  occurredAt: 'date',
};

/**
 * 작성일시 — 한국 시간 기준 표시(서버는 UTC ISO 로 준다).
 * 표기는 @bhops/shared 의 정본(kstDateTime)을 쓴다 — toLocaleString 은 실행
 * 환경의 로캘·타임존을 타서 보는 사람마다 다른 시각이 찍힌다.
 */
function fmtKst(iso: string | null): string {
  return kstDateTime(iso) ?? '–';
}

const th: CSSProperties = {
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '9px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};

const td: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};
const multiline: CSSProperties = { ...td, whiteSpace: 'pre-line' };

const control: CSSProperties = {
  padding: '6px 8px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

export function FaultRemediationsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // 고객사 리포트와 같은 권한 경계(VIEWER 는 읽기만).
  const canEdit = !!user && canEditAny(user.role);

  // 주소로 들어오는 두 가지:
  //   highlight — 고객사 리포트에서 넘어올 때 칠할 줄. 한 신고에 조치가 여러 건
  //               붙을 수 있어(재방문) **여러 줄이 칠해질 수 있다.**
  //   search    — 로봇 상세의 '연결하기' 가 시리얼을 실어 보낸다. 목록을 그 로봇
  //               것만 남겨야 연필을 눌러 리포트 번호를 넣을 수 있다.
  const [params] = useSearchParams();
  const highlight = params.get('highlight');
  //   serviceOrder — 통합 캘린더의 유지보수 일정을 누르면 그 서비스 오더로 만든 줄을 칠한다.
  const serviceOrderFocus = params.get('serviceOrder');
  const initialSearch = params.get('search') ?? '';

  //   status — 필드 대시보드의 '고장 조치 이력' 막대를 누르면 그 상태로 걸러 연다(2026-10-27).
  const initialStatus = params.get('status');
  //   sheetTab — 필드 대시보드의 보름 이슈 정리에서 그 달로 걸러 연다(2026-10-27).
  const initialTab = params.get('sheetTab');
  //   issueHalf — 대시보드 '로봇 운영 이슈'의 '26.10 상반기 조치 이력 보기'(2026-10-28). 그 화면이 센 줄과 같다.
  const initialHalf = params.get('issueHalf');
  //   reportNo — 고객사 리포트의 번호를 누르면 그 번호의 조치만(2026-10-28 오너 "번호로 조치 이력 찾기").
  const initialReportNo = params.get('reportNo');
  const [filters, setFilters] = useState<ListFaultRemediationsQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
    search: initialSearch || undefined,
    sheetTab: initialTab && /^\d{4}-\d{2}$/.test(initialTab) ? initialTab : undefined,
    reportNo: initialReportNo?.trim() || undefined,
    issueHalf:
      initialHalf && /^\d{4}-(0[1-9]|1[0-2])-[12]$/.test(initialHalf) ? initialHalf : undefined,
    status:
      initialStatus && initialStatus in REMEDIATION_STATUS_LABELS
        ? (initialStatus as RemediationStatus)
        : undefined,
  });
  const [searchDraft, setSearchDraft] = useState(initialSearch);
  const [editing, setEditing] = useState<FaultRemediationDto | null>(null);
  // 새로 작성(2026-10-27 오너 — "캘린더나 리포트 없이도 고장 조치 이력을 새롭게 작성할 수 있는 버튼").
  const [creating, setCreating] = useState(false);

  const [colSort, setColSort] = useState<ColumnSortState<FaultRemediationSortColumn> | null>(null);
  const listQuery: ListFaultRemediationsQuery = {
    ...filters,
    ...(colSort
      ? { orderBy: colSort.key, orderDir: columnSortDir(SORT_KIND[colSort.key], colSort.step) }
      : {}),
  };
  const q = useQuery({
    queryKey: ['fault-remediations', listQuery],
    queryFn: () => faultRemediationsApi.list(listQuery),
  });

  // 월 드롭다운 — 시트 탭이 매달 늘어나므로 목록을 서버에서 받는다(하드코딩 금지).
  const tabsQuery = useQuery({
    queryKey: ['fault-remediations', 'tabs'],
    queryFn: () => faultRemediationsApi.tabs(),
  });

  const items = q.data?.items ?? [];
  const counts = useMemo(() => q.data?.statusCounts, [q.data]);
  const duplicates = q.data?.duplicateReportNos ?? [];
  const duplicateSet = useMemo(
    () => new Set(duplicates.map((d) => d.reportNo.toUpperCase())),
    [duplicates],
  );

  const updateMutation = useMutation({
    // ⚠ 여기 칸을 빼먹으면 **화면에서는 고쳐지는데 저장이 안 된다.** 수정창이
    //   보낸 값을 이 함수가 다시 골라 담기 때문이다(2026-09-16에 robotSerial 이
    //   그렇게 누락됐다). 수정창의 onSave 와 이 목록이 **같이** 늘어야 한다.
    mutationFn: (v: {
      id: string;
      status: RemediationStatus;
      symptom: string;
      robotSerial: string | null;
      action: string | null;
      partsUsed: string | null;
      matchedReportNo?: string | null;
      issueCategory?: IssueCategory | null;
    }) =>
      faultRemediationsApi.update(v.id, {
        status: v.status,
        symptom: v.symptom,
        robotSerial: v.robotSerial,
        action: v.action,
        partsUsed: v.partsUsed,
        ...(v.matchedReportNo !== undefined ? { matchedReportNo: v.matchedReportNo } : {}),
        ...(v.issueCategory !== undefined ? { issueCategory: v.issueCategory } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fault-remediations'] });
      toast.success('조치 이력을 수정했습니다.');
      setEditing(null);
    },
    onError: (err) => toast.error(extractApiError(err), '조치 이력 수정 실패'),
  });

  const createMutation = useMutation({
    mutationFn: faultRemediationsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fault-remediations'] });
      toast.success('조치 이력을 작성했습니다.');
      setCreating(false);
    },
    onError: (err) => toast.error(extractApiError(err), '조치 이력 작성 실패'),
  });

  // 소프트 삭제 — 행은 남고 목록에서만 사라진다. 되돌림은 서버의 restore.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => faultRemediationsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fault-remediations'] });
      toast.success('조치 이력을 삭제했습니다.');
      setEditing(null);
    },
    onError: (err) => toast.error(extractApiError(err), '조치 이력 삭제 실패'),
  });

  // 칠한 줄 중 첫 번째를 눈에 보이게 올린다.
  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if ((highlight || serviceOrderFocus) && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlight, serviceOrderFocus, items]);

  const applySearch = () =>
    setFilters((f) => ({ ...f, search: searchDraft.trim() || undefined, page: 1 }));

  const sortableTh = (key: FaultRemediationSortColumn, label: string, style = th) => (
    <SortableTh
      label={label}
      kind={SORT_KIND[key]}
      step={colSort?.key === key ? colSort.step : null}
      onToggle={() => {
        setColSort((cur) => nextColumnSort(cur, key, SORT_KIND[key]));
        setFilters((f) => ({ ...f, page: 1 }));
      }}
      testId={`remediation-sort-th-${key}`}
      thStyle={style}
    />
  );

  // 쪽을 넘기면 표 머리로 올린다 — 아래 쪽 이동 줄에서 누르므로 그대로 두면 새 쪽의 끝을 보게 된다.
  const tableTopRef = useRef<HTMLDivElement | null>(null);
  const goPage = useCallback((page: number) => {
    setFilters((f) => (f.page === page ? f : { ...f, page }));
    tableTopRef.current?.scrollIntoView({ block: 'start' });
  }, []);

  return (
    <div style={fieldReportPage}>
      <FieldReportTabs />

      {/* 상태 카드 — 서버가 **필터와 무관하게** 전체를 센다. 카드는 "지금 무엇이
          걸려 있나"를 보는 고정 지표라 필터마다 흔들리면 기준이 사라진다.
          누르면 해당 상태로 목록만 좁힌다. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${REMEDIATION_ORDER.length}, minmax(0, 1fr))`,
          gap: 8,
        }}
        data-testid="remediation-status-summary"
      >
        {REMEDIATION_ORDER.map((st) => {
          const tone = REMEDIATION_TONE[st];
          return (
            <StatFilterCard
              key={st}
              icon={REMEDIATION_ICON[st]}
              color={tone.bar}
              activeBg={tone.bg}
              label={REMEDIATION_STATUS_LABELS[st]}
              value={counts?.[st] ?? 0}
              active={filters.status === st}
              onClick={() =>
                setFilters((f) => ({ ...f, status: f.status === st ? undefined : st, page: 1 }))
              }
            />
          );
        })}
      </div>

      {/* 겹친 리포트 번호(2026-10-28 오너 "리포트 넘버 겹치는 일이 발생하면 알 수 있는 기능") — 번호를 누르면
          그 번호의 조치만 남는다. 필터와 무관하게 전체를 본다. */}
      {duplicates.length > 0 && (
        <div
          role="alert"
          data-testid="remediation-duplicate-report-nos"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: '8px 12px',
            borderRadius: 'var(--r-card)',
            border: '1px solid var(--fg-danger)',
            background: 'var(--bg-danger-subtle)',
            fontSize: 13,
          }}
        >
          <Icon name="alert" size={14} aria-hidden style={{ color: 'var(--fg-danger)' }} />
          <span style={{ fontWeight: 600 }}>
            리포트 번호가 겹친 조치 이력이 있습니다. 같은 건의 재방문이 아니면 번호를 고쳐 주세요.
          </span>
          {duplicates.map((d) => (
            <button
              key={d.reportNo}
              type="button"
              onClick={() => setFilters((f) => ({ ...f, reportNo: d.reportNo, page: 1 }))}
              title="누르면 이 번호의 조치만 봅니다."
              style={{
                ...control,
                padding: '2px 8px',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'var(--fg-danger)',
                borderColor: 'var(--fg-danger)',
              }}
            >
              {d.reportNo} · {d.count}건
            </button>
          ))}
        </div>
      )}

      {/* 필터 줄 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select
          style={control}
          value={filters.status ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              status: (e.target.value || undefined) as RemediationStatus | undefined,
              page: 1,
            }))
          }
          aria-label="상태 필터"
        >
          <option value="">전체 상태</option>
          {REMEDIATION_ORDER.map((k) => (
            <option key={k} value={k}>
              {REMEDIATION_STATUS_LABELS[k]}
            </option>
          ))}
        </select>

        {/* 월 탭 — 시트가 매달 탭을 늘리므로 목록을 서버에서 받아 채운다. */}
        <select
          style={control}
          value={filters.sheetTab ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, sheetTab: e.target.value || undefined, page: 1 }))
          }
          aria-label="월 필터"
        >
          <option value="">전체 월</option>
          {(tabsQuery.data ?? []).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        {filters.issueHalf && (
          <button
            type="button"
            style={{
              ...control,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
            onClick={() => setFilters((f) => ({ ...f, issueHalf: undefined, page: 1 }))}
            title="대시보드 '로봇 운영 이슈'가 센 줄만 보고 있습니다. 누르면 풉니다."
          >
            {halfLabel(filters.issueHalf)} 이슈만
            <Icon name="x" size={12} aria-hidden />
          </button>
        )}

        {filters.reportNo && (
          <button
            type="button"
            style={{
              ...control,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
            onClick={() => setFilters((f) => ({ ...f, reportNo: undefined, page: 1 }))}
            title="이 리포트 번호의 조치만 보고 있습니다. 누르면 풉니다."
            data-testid="remediation-report-no-chip"
          >
            {filters.reportNo} 조치만
            <Icon name="x" size={12} aria-hidden />
          </button>
        )}

        <input
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applySearch();
          }}
          placeholder="매장, 증상, 조치, 리포트 번호로 검색"
          aria-label="매장, 증상, 조치, 리포트 번호로 검색"
          style={{ ...control, width: 240 }}
        />
        <button type="button" style={{ ...control, cursor: 'pointer' }} onClick={applySearch}>
          검색
        </button>

        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          총 {q.data?.total ?? 0} 건
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="remediation-create"
            style={{
              ...control,
              cursor: 'pointer',
              background: 'var(--fg-brand)',
              color: 'var(--bg-surface)',
              borderColor: 'var(--fg-brand)',
              fontWeight: 600,
            }}
          >
            + 새 조치 이력
          </button>
        )}
      </div>

      {/* 표 */}
      <div
        ref={tableTopRef}
        style={{
          scrollMarginTop: 16,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          overflow: 'auto',
          background: 'var(--bg-surface)',
        }}
      >
        {/* minWidth 가 없으면 좁은 창에서 width:100% 가 열을 글자 단위로 쪼갠다.
            열이 10개라 이 아래로는 줄여도 못 읽는다 — 감싼 div 가 가로로 스크롤한다. */}
        <table style={{ width: '100%', minWidth: 1200, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 4, padding: 0 }} aria-hidden />
              {sortableTh('status', '상태')}
              {sortableTh('issueCategory', '구분')}
              {sortableTh('reportNo', '리포트 번호')}
              {sortableTh('store', '매장')}
              {sortableTh('robot', '로봇')}
              {sortableTh('errorCode', '오류 코드')}
              <th style={{ ...th, width: '24%' }}>증상 요약</th>
              <th style={{ ...th, width: '22%' }}>조치 내용</th>
              <th style={th}>사진</th>
              {sortableTh('occurredAt', '발생 일시')}
              <th style={th}>편집</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td style={{ ...td, color: 'var(--text-secondary)' }} colSpan={12}>
                  불러오는 중…
                </td>
              </tr>
            )}

            {q.isError && (
              <tr>
                <td style={{ ...td, color: 'var(--fg-danger)' }} colSpan={12}>
                  {extractApiError(q.error)}
                </td>
              </tr>
            )}

            {!q.isLoading && !q.isError && items.length === 0 && (
              <tr>
                <td style={{ ...td, color: 'var(--text-secondary)' }} colSpan={12}>
                  아직 기록이 없습니다. 필드팀 유지보수 시트와 연결되면 여기에 쌓입니다.
                </td>
              </tr>
            )}

            {items.map((r, idx) => {
              const tone = REMEDIATION_TONE[r.status];
              const isLit = (x: FaultRemediationDto) =>
                (!!highlight && x.matchedReport?.sheetRowKey === highlight) ||
                (!!serviceOrderFocus && x.serviceOrderId === serviceOrderFocus);
              const lit = isLit(r);
              const firstLit = lit && items.findIndex(isLit) === idx;
              return (
                <tr
                  key={r.id}
                  ref={firstLit ? highlightRef : undefined}
                  style={
                    lit
                      ? { background: 'var(--bg-warning-subtle)', scrollMarginTop: 80 }
                      : undefined
                  }
                >
                  <td style={{ padding: 0, background: tone.bar }} aria-hidden />
                  <td style={td}>
                    <span style={{ ...pill, background: tone.bg, color: tone.fg }}>
                      {REMEDIATION_STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <CategoryCell category={r.issueCategory} auto={r.issueCategoryAuto} />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <MatchCell
                      match={r.matchedReport}
                      reportNo={r.reportNo}
                      duplicate={!!r.reportNo && duplicateSet.has(r.reportNo.toUpperCase())}
                    />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.storeName?.trim() ? r.storeName : '–'}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <RobotCell model={r.robotModel} serial={r.robotSerial} robots={r.robots} />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.errorCode?.trim() ? r.errorCode : '–'}
                  </td>
                  {/* 2026-10-22 오너 — 작성 창에서 넣은 줄바꿈이 표에서도 보이게. */}
                  <td style={multiline}>{r.symptom}</td>
                  <td style={multiline}>{r.action?.trim() ? r.action : '–'}</td>
                  <td style={td}>
                    <PhotoLinks urls={r.photoUrls} />
                  </td>
                  <td
                    style={{ ...td, whiteSpace: 'nowrap' }}
                    title={`작성 일시 ${fmtKst(r.createdAt)}`}
                  >
                    {fmtKst(r.occurredAt)}
                  </td>
                  <td style={td}>
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      disabled={!canEdit}
                      title={canEdit ? '조치 내용과 상태 수정' : '수정 권한이 없습니다'}
                      aria-label="조치 이력 수정"
                      data-testid="remediation-edit"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: canEdit ? 'pointer' : 'default',
                        color: canEdit ? 'var(--fg-muted)' : 'var(--border-default)',
                        padding: 2,
                        lineHeight: 0,
                      }}
                    >
                      <Icon name="pen" size={14} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 쪽 이동(2026-10-28 오너 "30건 단위로 페이지 나눠줘. 페이지 이동하는거도 없네"). */}
      {(q.data?.total ?? 0) > 0 && (
        <Pagination
          page={filters.page ?? 1}
          pageSize={PAGE_SIZE}
          total={q.data?.total ?? 0}
          onPageChange={goPage}
        />
      )}

      {editing && (
        <EditDialog
          // 기록마다 창을 새로 만든다. key 가 없으면 React 가 같은 인스턴스를
          // 재사용해 확인 단계와 입력 초안이 다음 기록으로 넘어간다
          // (고객사 리포트에서 실제로 겪은 사고).
          key={editing.id}
          row={editing}
          busy={updateMutation.isPending}
          deleting={deleteMutation.isPending}
          canDelete={canEdit}
          onCancel={() => setEditing(null)}
          onSave={(v) => updateMutation.mutate({ id: editing.id, ...v })}
          onDelete={() => deleteMutation.mutate(editing.id)}
        />
      )}

      {creating && (
        <CreateDialog
          busy={createMutation.isPending}
          onCancel={() => setCreating(false)}
          onSave={(v) => createMutation.mutate(v)}
        />
      )}
    </div>
  );
}

/**
 * 연결 칸이 실제로 바뀌었을 때만 값을 돌려준다(안 바뀌었으면 `undefined`).
 *
 * 매번 보내면 자동 매칭이 붙여 준 근거가 **손도 안 댔는데 '직접 지정'** 으로
 * 덮인다 — 나중에 "이게 추측이었나?" 를 물을 수 없게 된다.
 */
/** '2026-10-1' → '2026년 10월 상반기' */
function halfLabel(key: string): string {
  const [y, m, h] = key.split('-');
  return `${y}년 ${Number(m)}월 ${h === '1' ? '상반기' : '하반기'}`;
}

function matchChanged(row: FaultRemediationDto, draft: string): string | null | undefined {
  const before = currentReportNo(row);
  const after = draft.trim();
  if (before === after) return undefined;
  return after === '' ? null : after;
}

/** 이 줄의 리포트 번호 — 이어진 리포트가 있으면 그 번호, 없으면 적어 둔 번호(등록 안 된 번호). */
function currentReportNo(row: FaultRemediationDto): string {
  return row.matchedReport?.sheetRowKey ?? row.reportNo ?? '';
}

/**
 * 리포트 번호 — 이어진 고객사 리포트가 있으면 그 링크, 없으면 적어 둔 번호만(2026-10-27 오너 — 리포트가 등록 안
 * 돼 있어도 번호는 남는다. 노션 Problem No. 가 이 자리에 들어간다).
 *
 * **추측으로 붙은 연결(매장·시간)은 확정처럼 보이면 안 된다.** 번호 아래에
 * 근거를 작게 깔아, 읽는 사람이 믿을지 말지 스스로 정하게 한다.
 */
function MatchCell({
  match,
  reportNo,
  duplicate = false,
}: {
  match: FaultRemediationDto['matchedReport'];
  reportNo: string | null;
  /** 다른 조치 이력과 번호가 겹쳤다(목록 위 알림과 같은 판정). */
  duplicate?: boolean;
}) {
  if (!match) {
    if (!reportNo) return <span style={{ color: 'var(--text-secondary)' }}>–</span>;
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
        <span style={{ fontWeight: 600, color: duplicate ? 'var(--fg-danger)' : undefined }}>
          {reportNo}
        </span>
        {duplicate ? (
          <span
            style={{ fontSize: 11, color: 'var(--fg-danger)', fontWeight: 600 }}
            title="다른 조치 이력도 이 번호를 씁니다. 연필로 번호를 고칠 수 있습니다."
            data-testid="remediation-report-no-duplicate"
          >
            번호 겹침
          </span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>리포트 미등록</span>
        )}
      </span>
    );
  }
  const guessed = match.matchedBy === 'STORE_TIME';
  return (
    <span
      style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}
      title={match.summary}
    >
      <Link
        to={`/field/customer-reports?highlight=${encodeURIComponent(match.sheetRowKey)}`}
        style={{
          fontWeight: 600,
          color: guessed ? 'var(--fg-default)' : 'var(--fg-brand)',
          textDecoration: 'none',
        }}
        title="이 조치가 다룬 신고 보기"
      >
        {match.sheetRowKey}
      </Link>
      <span
        style={{ fontSize: 11, color: guessed ? 'var(--dday-warning)' : 'var(--text-secondary)' }}
      >
        {REMEDIATION_MATCH_SOURCE_LABELS[match.matchedBy]}
      </span>
    </span>
  );
}

/**
 * 로봇 — **시리얼이 위, 모델이 아래**(오너 결정 2026-09-14).
 *
 * 시리얼이 그 로봇 한 대를 가리키는 값이라 찾을 때 먼저 눈에 들어와야 한다.
 * 모델(GEN.2(M12) …)은 같은 값이 여러 대에 걸치는 분류라 보조로 깐다.
 *
 * **등록된 로봇일 때만 링크**다. 시트 시리얼은 자유 입력이라 `t33` 같은 값이
 * 섞여 있고, 그런 걸 링크로 만들면 누를 때마다 404 다. 안 걸린 시리얼은 그냥
 * 글자로 남는다 — 링크가 없는 게 곧 "아직 등록 안 됨" 이다.
 *
 * **여러 대를 한 줄에 적는다**(`321, 322`). 한 번 방문에 여러 대를 손보면 시트가
 * 그렇게 적는다(오너 확인 2026-09-16). 서버가 낱개로 나눠(`robots`) 각각 짝을
 * 찾아 주므로, 여기서는 **등록된 것만 링크**로 그리고 쉼표로 이어 붙인다.
 * 종전에는 칸 전체를 한 대의 시리얼로 봐서 `321, 322` 는 통째로 링크가 안 걸렸다.
 */
function RobotCell({
  model,
  serial,
  robots,
}: {
  model: string | null;
  serial: string | null;
  robots: FaultRemediationDto['robots'];
}) {
  if (!model?.trim() && !serial?.trim()) {
    return <span style={{ color: 'var(--text-secondary)' }}>–</span>;
  }
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <span>
        {robots.length === 0 ? (
          // 나눌 게 없다 — 원문이 공백뿐인 경우. 모델만 보여 준다.
          <span style={{ color: 'var(--text-secondary)' }}>–</span>
        ) : (
          robots.map((b, i) => (
            <span key={b.serial}>
              {i > 0 && <span style={{ color: 'var(--text-secondary)' }}>, </span>}
              {b.robotId ? (
                <Link
                  to={`/robots/${b.robotId}`}
                  style={{ color: 'var(--fg-brand)', fontWeight: 600, textDecoration: 'none' }}
                  title="로봇 상세로"
                >
                  {b.serial}
                </Link>
              ) : (
                <span title="등록된 로봇이 아닙니다">{b.serial}</span>
              )}
            </span>
          ))
        )}
      </span>
      {model?.trim() && (
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{model}</span>
      )}
    </span>
  );
}

/**
 * 현장 사진 — 시트가 한 칸에 **여러 링크**를 넣는다(실측). 그래서 배열이다.
 * 파일을 콘솔로 복사하지 않고 링크만 연다(고객사 리포트와 같은 규칙).
 */
function PhotoLinks({ urls }: { urls: string[] }) {
  if (!urls.length) return <span style={{ color: 'var(--text-secondary)' }}>–</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {urls.map((u, i) => (
        <a
          key={u}
          href={u}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--fg-brand)', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          사진{urls.length > 1 ? ` ${i + 1}` : ''}
        </a>
      ))}
    </span>
  );
}

function EditDialog({
  row,
  busy,
  deleting,
  canDelete,
  onCancel,
  onSave,
  onDelete,
}: {
  row: FaultRemediationDto;
  busy: boolean;
  deleting: boolean;
  canDelete: boolean;
  onCancel: () => void;
  onSave: (v: {
    status: RemediationStatus;
    symptom: string;
    robotSerial: string | null;
    action: string | null;
    partsUsed: string | null;
    matchedReportNo?: string | null;
    issueCategory?: IssueCategory | null;
  }) => void;
  onDelete: () => void;
}) {
  const [status, setStatus] = useState<RemediationStatus>(row.status);
  const [symptom, setSymptom] = useState(row.symptom);
  // 로봇 시리얼 — 시트 값이 비거나 틀린 경우가 흔해 여기서 고친다.
  // 여러 대면 쉼표나 공백으로 나눠 적는다(`321, 322`).
  const [robotSerial, setRobotSerial] = useState(row.robotSerial ?? '');
  const [action, setAction] = useState(row.action ?? '');
  const [partsUsed, setPartsUsed] = useState(row.partsUsed ?? '');
  // 리포트 번호. 등록된 리포트면 잇고, 아니면 번호만 남는다. 비우면 연결을 끊는다.
  const [reportNo, setReportNo] = useState(currentReportNo(row));
  // 구분 — '' 는 자동(키워드).
  const initialCategory: IssueCategory | '' = row.issueCategoryAuto ? '' : row.issueCategory;
  const [category, setCategory] = useState<IssueCategory | ''>(initialCategory);
  // 삭제는 한 번 더 묻는다. 브라우저 confirm 을 쓰지 않는 이유 — 이 창 안에서
  // 무엇을 지우는지(증상)를 같이 보여줘야 잘못 누른 걸 알아챌 수 있다.
  const [confirming, setConfirming] = useState(false);

  // 빈 증상은 저장하지 않는다 — 목록에서 무슨 건인지 알 수 없게 된다.
  const symptomOk = symptom.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="조치 이력 수정"
      // 배경을 눌러 닫기 — 누른 곳과 뗀 곳이 **둘 다 배경**일 때만 닫는다.
      // 입력칸에서 글자를 드래그하다 배경에서 손을 떼도 닫히던 사고를 막는다.
      {...useBackdropDismiss(onCancel)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      {/* 안쪽 패널에 stopPropagation 이 필요 없다 — 배경 판정이
          `e.target === e.currentTarget` 이라 여기서 난 이벤트는 애초에 안 걸린다. */}
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-card)',
          padding: 20,
          width: 420,
          maxHeight: '86vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <h2 className="kuun-h3" style={{ margin: 0 }}>
          조치 이력 수정
        </h2>

        {/* 시트가 준 값 중 화면에서 고치지 않는 것들 — 무슨 건인지 알아보게 띄운다. */}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          발생 {fmtKst(row.occurredAt)} · 작성 {fmtKst(row.createdAt)}
          {row.author ? ` · ${row.author}` : ''}
          {row.storeName ? ` · ${row.storeName}` : ''}
          {row.robotModel ? ` · ${row.robotModel}` : ''}
          {row.errorCode ? ` · 오류 ${row.errorCode}` : ''}
        </div>

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          로봇 시리얼
          <input
            style={{ ...control, width: '100%', marginTop: 4 }}
            maxLength={200}
            value={robotSerial}
            onChange={(e) => setRobotSerial(e.target.value)}
            placeholder="예: 321 또는 321, 322"
            aria-label="로봇 시리얼"
            data-testid="remediation-serial-input"
          />
        </label>
        {/* 여러 대를 한 번에 손본 경우가 있어 쉼표·공백으로 나눠 적는다.
            등록된 시리얼만 목록에서 링크가 걸린다 — 안 걸리면 오타이거나
            아직 등록 안 된 로봇이다. */}
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: -6 }}>
          여러 대면 쉼표나 공백으로 나눠 적습니다. 등록된 로봇만 목록에서 링크가 걸립니다.
        </span>

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          증상 요약
          <textarea
            style={{ ...control, width: '100%', marginTop: 4, resize: 'vertical' }}
            rows={3}
            maxLength={2000}
            value={symptom}
            onChange={(e) => setSymptom(e.target.value)}
            aria-label="증상 요약"
            data-testid="remediation-symptom-input"
          />
        </label>
        {!symptomOk && (
          <span style={{ fontSize: 11, color: 'var(--fg-danger)' }}>증상은 비울 수 없습니다.</span>
        )}

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          조치 내용
          <textarea
            style={{ ...control, width: '100%', marginTop: 4, resize: 'vertical' }}
            rows={3}
            maxLength={2000}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label="조치 내용"
          />
        </label>

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          사용 부품
          <input
            style={{ ...control, width: '100%', marginTop: 4 }}
            maxLength={500}
            value={partsUsed}
            onChange={(e) => setPartsUsed(e.target.value)}
            aria-label="사용 부품"
          />
        </label>

        <CategorySelect
          value={category}
          onChange={setCategory}
          autoValue={row.issueCategoryAuto ? row.issueCategory : undefined}
        />

        {/* 연결 — 자동 매칭이 추측으로 잘못 붙였을 때의 출구다. 번호를 고치면
            '직접 지정' 이 되고, 비우면 연결이 끊긴다. 등록된 리포트가 없는 번호도 남는다(2026-10-27). */}
        <label style={{ fontSize: 12, fontWeight: 600 }}>
          리포트 번호{' '}
          {row.matchedReport && (
            <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>
              ({REMEDIATION_MATCH_SOURCE_LABELS[row.matchedReport.matchedBy]})
            </span>
          )}
          <input
            style={{ ...control, width: '100%', marginTop: 4 }}
            maxLength={200}
            value={reportNo}
            onChange={(e) => setReportNo(e.target.value)}
            placeholder="비우면 연결이 풀립니다"
            aria-label="리포트 번호"
            data-testid="remediation-report-no"
          />
        </label>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: -6 }}>
          고객사 리포트에 있는 번호면 그 리포트와 이어지고, 없는 번호도 그대로 남습니다.
        </span>

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          상태
          <select
            style={{ ...control, width: '100%', marginTop: 4 }}
            value={status}
            onChange={(e) => setStatus(e.target.value as RemediationStatus)}
          >
            {REMEDIATION_ORDER.map((k) => (
              <option key={k} value={k}>
                {REMEDIATION_STATUS_LABELS[k]}
              </option>
            ))}
          </select>
        </label>

        {confirming && (
          <div
            style={{
              border: '1px solid var(--border-danger-saturated)',
              background: 'var(--bg-danger-subtle)',
              color: 'var(--fg-on-danger-tint)',
              borderRadius: 'var(--r-card)',
              padding: '8px 10px',
              fontSize: 12,
              lineHeight: 1.5,
            }}
            role="alert"
          >
            <b>{row.symptom}</b>
            <br />이 기록을 목록에서 지웁니다. 시트 원본은 그대로 남습니다.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: 4,
          }}
        >
          {canDelete && (
            <button
              type="button"
              data-testid="remediation-delete"
              disabled={deleting}
              onClick={() => (confirming ? onDelete() : setConfirming(true))}
              style={{
                ...control,
                marginRight: 'auto', // 저장과 멀리 떨어뜨린다 — 오클릭 방지
                cursor: deleting ? 'default' : 'pointer',
                background: confirming ? 'var(--fg-danger)' : 'transparent',
                color: confirming ? 'var(--bg-surface)' : 'var(--fg-danger)',
                borderColor: 'var(--fg-danger)',
                fontWeight: 600,
                opacity: deleting ? 0.6 : 1,
              }}
            >
              {deleting ? '삭제 중…' : confirming ? '삭제 확인' : '삭제'}
            </button>
          )}
          <button
            type="button"
            style={{ ...control, cursor: 'pointer' }}
            onClick={() => (confirming ? setConfirming(false) : onCancel())}
          >
            취소
          </button>
          <button
            type="button"
            disabled={busy || !symptomOk}
            onClick={() =>
              onSave({
                status,
                symptom: symptom.trim(),
                robotSerial: robotSerial.trim() || null,
                action: action.trim() || null,
                partsUsed: partsUsed.trim() || null,
                // 안 바뀌었으면 아예 안 보낸다 — 보내면 자동 매칭 근거가
                // '직접 지정' 으로 덮인다(고치지도 않았는데).
                matchedReportNo: matchChanged(row, reportNo),
                // 안 바꿨으면 안 보낸다. '' 는 자동으로 돌리기(null).
                ...(category !== initialCategory ? { issueCategory: category || null } : {}),
              })
            }
            style={{
              ...control,
              cursor: busy || !symptomOk ? 'default' : 'pointer',
              background: 'var(--fg-brand)',
              color: 'var(--bg-surface)',
              borderColor: 'var(--fg-brand)',
              opacity: busy || !symptomOk ? 0.6 : 1,
            }}
          >
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 새 조치 이력 작성(2026-10-27 오너 — "캘린더나 리포트 없이도 고장 조치 이력을 새롭게 작성할 수 있는 버튼").
 *
 * 시트 · 노션 · 캘린더에서 온 줄과 같은 표에 쌓인다. 리포트 번호는 고객사 리포트에 있으면 그 리포트와 잇고,
 * 없으면 번호만 남는다. 구분은 비워 두면 증상 · 조치 글의 키워드로 자동 분류한다(고를 값이 미리 보인다).
 */
function CreateDialog({
  busy,
  onCancel,
  onSave,
}: {
  busy: boolean;
  onCancel: () => void;
  onSave: (v: CreateFaultRemediationDto) => void;
}) {
  // 문제 발생일 칸의 처음 값 — 오늘 한국 날짜.
  const [occurredAt, setOccurredAt] = useState(() => kstTodayYmd());
  const [storeName, setStoreName] = useState('');
  const [store, setStore] = useState<PickedStore | null>(null);
  const [pickedSerials, setPickedSerials] = useState<string[]>([]);
  // 고른 매장의 로봇 수(불러오기 전 undefined). 로봇이 있으면 체크로 고르고 모델 · 시리얼 칸은 숨긴다
  // (2026-10-28 오너 "이 입력창은 의미 없지 않나 이제? 이거로 고를거잖아").
  const [storeRobotCount, setStoreRobotCount] = useState<number | undefined>(undefined);
  const manualRobot = !store || storeRobotCount === 0;
  const [robotModel, setRobotModel] = useState('');
  const [robotSerial, setRobotSerial] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [symptom, setSymptom] = useState('');
  const [action, setAction] = useState('');
  const [partsUsed, setPartsUsed] = useState('');
  const [reportNo, setReportNo] = useState('');
  // 리포트 번호는 문제 발생일로 자동(2026-10-28 오너 — "P261026-01, 그 후는 -02"). 사람이 고치면 그 값을 둔다
  // (고객사 리포트의 번호를 넣어 그 신고와 이을 때). 비워서 저장해도 서버가 같은 규칙으로 매긴다.
  const [reportNoTouched, setReportNoTouched] = useState(false);
  const nextNo = useQuery({
    queryKey: ['fault-remediations', 'next-report-no', occurredAt],
    queryFn: () => faultRemediationsApi.nextReportNo(occurredAt),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(occurredAt),
    staleTime: 0,
  });
  useEffect(() => {
    if (!reportNoTouched && nextNo.data) setReportNo(nextNo.data);
  }, [nextNo.data, reportNoTouched]);
  // 직접 적은 번호가 이미 다른 조치 이력의 번호인지(2026-10-28 오너 "리포트 넘버 겹치는 일이 발생하면 알 수 있게").
  // 고객사 리포트에 이어진 번호면 재방문이라 괜찮다 — 이어지지 않은 번호만 알린다.
  const typedNo = useDebounce(reportNoTouched ? reportNo.trim() : '', 300);
  const usedNo = useQuery({
    queryKey: ['fault-remediations', 'report-no-used', typedNo],
    queryFn: () => faultRemediationsApi.list({ reportNo: typedNo, page: 1, pageSize: 1 }),
    enabled: typedNo.length > 0,
  });
  const clashCount =
    typedNo && usedNo.data && usedNo.data.total > 0 && !usedNo.data.items[0]?.matchedReport
      ? usedNo.data.total
      : 0;
  const [status, setStatus] = useState<RemediationStatus>(RemediationStatus.IN_PROGRESS);
  const [category, setCategory] = useState<IssueCategory | ''>('');

  const symptomOk = symptom.trim().length > 0;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(occurredAt);
  const ok = symptomOk && dateOk && !busy;
  const autoCategory = classifyIssueCategory({ symptom, action });
  const field: CSSProperties = { ...control, width: '100%', marginTop: 4 };
  const label: CSSProperties = { fontSize: 12, fontWeight: 600 };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="새 조치 이력"
      {...useBackdropDismiss(onCancel)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-card)',
          padding: 20,
          width: 480,
          maxHeight: '86vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <h2 className="kuun-h3" style={{ margin: 0 }}>
          새 조치 이력
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* 2026-10-28 오너 — 작성 일시가 아니라 문제가 난 날을 적는다(시간은 고르지 않는다 — "일자만 고르게 해").
              작성 일시는 저장한 때로 자동으로 남는다. */}
          <label style={label}>
            문제 발생일
            <input
              type="date"
              style={field}
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              aria-label="문제 발생일"
            />
          </label>
          <label style={label}>
            매장
            <StoreSuggestInput
              style={field}
              value={storeName}
              picked={store}
              onChange={(text, picked) => {
                setStoreName(text);
                if (picked?.id !== store?.id) {
                  setStore(picked);
                  setPickedSerials([]);
                  setStoreRobotCount(undefined);
                  setRobotSerial('');
                  setRobotModel('');
                }
              }}
            />
          </label>
          {manualRobot && (
            <>
              <label style={label}>
                로봇 모델
                <input
                  style={field}
                  maxLength={100}
                  list="remediation-model-options"
                  value={robotModel}
                  onChange={(e) => setRobotModel(e.target.value)}
                  placeholder="예: Gen1"
                  aria-label="로봇 모델"
                />
                <datalist id="remediation-model-options">
                  <option value="Gen1" />
                  <option value="Gen2" />
                  <option value="Zephyr" />
                </datalist>
              </label>
              <label style={label}>
                로봇 시리얼
                <input
                  style={field}
                  maxLength={200}
                  value={robotSerial}
                  onChange={(e) => setRobotSerial(e.target.value)}
                  placeholder="예: 321 또는 321, 322"
                  aria-label="로봇 시리얼"
                />
              </label>
            </>
          )}
          <label style={label}>
            오류 코드
            <input
              style={field}
              maxLength={100}
              value={errorCode}
              onChange={(e) => setErrorCode(e.target.value)}
              aria-label="오류 코드"
            />
          </label>
          <label style={label}>
            리포트 번호
            <input
              style={field}
              maxLength={200}
              value={reportNo}
              onChange={(e) => {
                setReportNo(e.target.value);
                setReportNoTouched(true);
              }}
              placeholder="비우면 문제 발생일로 자동"
              aria-label="리포트 번호"
              data-testid="remediation-create-report-no"
            />
          </label>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: -6 }}>
          리포트 번호는 문제 발생일로 자동으로 채워집니다(P261026-01, -02 …). 고객사 리포트의 번호로
          바꾸면 그 리포트와 이어집니다.
        </span>
        {clashCount > 0 && (
          <span
            role="alert"
            data-testid="remediation-create-report-no-clash"
            style={{ fontSize: 12, color: 'var(--fg-danger)', marginTop: -6 }}
          >
            {typedNo}은(는) 이미 다른 조치 이력 {clashCount}건에 쓴 번호입니다. 같은 건의 재방문이
            아니면 번호를 바꿔 주세요.
          </span>
        )}

        {store && (
          <div style={label}>
            {store.label}의 로봇{' '}
            <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>
              — 함께 고장 난 로봇이 있으면 여러 대 고릅니다
            </span>
            <div style={{ marginTop: 6 }}>
              <StoreRobotChecklist
                storeId={store.id}
                onLoaded={setStoreRobotCount}
                selected={pickedSerials}
                onChange={(serials, models) => {
                  setPickedSerials(serials);
                  setRobotSerial(serials.join(', '));
                  if (models.length > 0) setRobotModel(models.join(', '));
                }}
              />
            </div>
          </div>
        )}

        <label style={label}>
          증상 요약
          <textarea
            style={{ ...field, resize: 'vertical' }}
            rows={3}
            maxLength={2000}
            value={symptom}
            onChange={(e) => setSymptom(e.target.value)}
            aria-label="증상 요약"
            data-testid="remediation-create-symptom"
          />
        </label>

        <label style={label}>
          조치 내용
          <textarea
            style={{ ...field, resize: 'vertical' }}
            rows={3}
            maxLength={2000}
            value={action}
            onChange={(e) => setAction(e.target.value)}
            aria-label="조치 내용"
          />
        </label>

        <label style={label}>
          사용 부품
          <input
            style={field}
            maxLength={500}
            value={partsUsed}
            onChange={(e) => setPartsUsed(e.target.value)}
            aria-label="사용 부품"
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <CategorySelect
            value={category}
            onChange={setCategory}
            autoValue={symptom.trim() ? autoCategory : undefined}
          />
          <label style={label}>
            상태
            <select
              style={field}
              value={status}
              onChange={(e) => setStatus(e.target.value as RemediationStatus)}
            >
              {REMEDIATION_ORDER.map((k) => (
                <option key={k} value={k}>
                  {REMEDIATION_STATUS_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button type="button" style={{ ...control, cursor: 'pointer' }} onClick={onCancel}>
            취소
          </button>
          <button
            type="button"
            disabled={!ok}
            data-testid="remediation-create-save"
            onClick={() =>
              onSave({
                // 그날 한국 0시 — 목록 · 대시보드가 한국 날짜로 센다.
                occurredAt: new Date(`${occurredAt}T00:00:00+09:00`).toISOString(),
                storeName: storeName.trim() || null,
                robotModel: robotModel.trim() || null,
                robotSerial: robotSerial.trim() || null,
                errorCode: errorCode.trim() || null,
                symptom: symptom.trim(),
                action: action.trim() || null,
                partsUsed: partsUsed.trim() || null,
                reportNo: reportNo.trim() || null,
                status,
                issueCategory: category || null,
              })
            }
            style={{
              ...control,
              cursor: ok ? 'pointer' : 'default',
              background: 'var(--fg-brand)',
              color: 'var(--bg-surface)',
              borderColor: 'var(--fg-brand)',
              opacity: ok ? 1 : 0.6,
            }}
          >
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
